import { Footprints, Inbox, Send, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';

import type { ShellContext } from '@/components/layout/AppShell';
import { InlineAdd } from '@/components/tasks/QuickAdd';
import { TaskList } from '@/components/tasks/TaskList';
import { sortTasks, TaskSortBar, useTaskSort } from '@/components/tasks/TaskSort';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  useAssignedByMe,
  useAssignedToMe,
  useBacklog,
  useInvolved,
  useCreateTask,
} from '@/hooks/useTasks';
import type { Paginated, TaskSummary } from '@/lib/api/types';
import { toDayString } from '@/lib/dates';
import { useT, type MessageKey } from '@/lib/i18n';
import { Page } from '@/routes/Today';
import { useTaskActions } from '@/routes/useTaskActions';

const PAGE_SIZE = 50;

/**
 * The shared shape of every flat, paginated task screen.
 *
 * Backlog, Completed and the two delegation inboxes differ only in their query,
 * their empty state, and whether they can be added to — so they are one
 * component rather than four near-identical ones.
 */
function PaginatedTaskScreen({
  title,
  useData,
  emptyTitle,
  emptyHint,
  emptyIcon,
  showDate = true,
  onCreate,
}: {
  title: MessageKey;
  useData: (offset: number) => UseQueryResult<Paginated<TaskSummary>>;
  emptyTitle: MessageKey;
  emptyHint: MessageKey;
  emptyIcon: React.ReactNode;
  showDate?: boolean;
  onCreate?: (title: string, assignedToUserId?: string) => void;
}) {
  const t = useT();
  const { currentUser, openTask } = useOutletContext<ShellContext>();
  const [offset, setOffset] = useState(0);
  const { data, isPending, error, refetch } = useData(offset);
  const actions = useTaskActions(openTask);
  const today = toDayString(new Date());
  const [sort, setSort] = useTaskSort(title);

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  if (!data) return null;

  return (
    <Page>
      <header className="mb-5 flex items-baseline gap-2">
        <h1 className="text-xl font-semibold tracking-tight">{t(title)}</h1>
        {data.meta.total > 0 ? (
          <span className="text-sm tabular-nums text-content-subtle">
            {data.meta.total}
          </span>
        ) : null}
      </header>

      {data.items.length > 1 ? (
        <div className="mb-3">
          <TaskSortBar sort={sort} onChange={setSort} />
        </div>
      ) : null}

      {data.items.length === 0 ? (
        <EmptyState icon={emptyIcon} title={t(emptyTitle)} hint={t(emptyHint)} />
      ) : (
        <TaskList
          tasks={sortTasks(data.items, sort)}
          today={today}
          currentUserId={currentUser.id}
          actions={actions}
          showDate={showDate}
        />
      )}

      {onCreate ? (
        <div className="mt-2">
          <InlineAddBinding onCreate={onCreate} />
        </div>
      ) : null}

      {data.meta.total > PAGE_SIZE ? (
        <nav
          className="mt-6 flex items-center gap-2"
          aria-label={t('common.pagination')}
        >
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            {t('common.previous')}
          </Button>
          <span className="text-xs tabular-nums text-content-subtle">
            {t('common.range', {
              from: offset + 1,
              to: Math.min(offset + PAGE_SIZE, data.meta.total),
              total: data.meta.total,
            })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={!data.meta.has_more}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            {t('common.next')}
          </Button>
        </nav>
      ) : null}
    </Page>
  );
}

function InlineAddBinding({
  onCreate,
}: {
  onCreate: (title: string, assignedToUserId?: string) => void;
}) {
  const createTask = useCreateTask();
  return (
    <InlineAdd
      isPending={createTask.isPending}
      onCreate={(input) =>
        onCreate(input.title, input.assigned_to_user_id ?? undefined)
      }
    />
  );
}

export function InvolvedRoute() {
  return (
    <PaginatedTaskScreen
      title="nav.involved"
      useData={useInvolved}
      emptyTitle="involved.empty.title"
      emptyHint="involved.empty.hint"
      emptyIcon={<Footprints className="h-5 w-5" />}
    />
  );
}

export function BacklogRoute() {
  const createTask = useCreateTask();
  return (
    <PaginatedTaskScreen
      title="nav.backlog"
      useData={useBacklog}
      emptyIcon={<Inbox className="h-5 w-5" />}
      emptyTitle="backlog.empty.title"
      emptyHint="backlog.empty.hint"
      showDate={false}
      onCreate={(title, assignedToUserId) =>
        createTask.mutate({
          title,
          schedule: 'backlog',
          ...(assignedToUserId ? { assigned_to_user_id: assignedToUserId } : {}),
        })
      }
    />
  );
}

export function AssignedToMeRoute() {
  return (
    <PaginatedTaskScreen
      title="nav.assignedToMe"
      useData={useAssignedToMe}
      emptyIcon={<UserCheck className="h-5 w-5" />}
      emptyTitle="assignedToMe.empty.title"
      emptyHint="assignedToMe.empty.hint"
    />
  );
}

export function AssignedByMeRoute() {
  return (
    <PaginatedTaskScreen
      title="nav.assignedByMe"
      useData={useAssignedByMe}
      emptyIcon={<Send className="h-5 w-5" />}
      emptyTitle="assignedByMe.empty.title"
      emptyHint="assignedByMe.empty.hint"
    />
  );
}
